import { Field as UnionField } from '@xo-union/tk-component-fields';
import { FieldProps, FormikErrors, getIn, useFormikContext } from 'formik';
import React from 'react';

import { InputState, sanitizeHtml } from './common';
import { AiButton, buildField } from '../../../components/AiButton/AiButton';
import styles from './field.scss';

export interface FormField extends FieldProps {
  focus: boolean;
  showAiButton: boolean;
  subText?: string;
  displayInitialErrors: boolean;
  inputRef?: unknown;
  onChange?: (event: any) => void;
  onBlur?: () => void;
}

export const Field: React.FC<FormField> = props => {
  const {
    field,
    form: { touched, errors, setFieldTouched, values },
    subText,
    displayInitialErrors = false,
    inputRef,
    onChange,
    onBlur,
    showAiButton = false,
    ...rest
  } = props;

  let text: string | FormikErrors<any> | undefined;
  const fieldTouched = getIn(touched, field.name) || displayInitialErrors;
  const error = getIn(errors, field.name);
  const { setFieldValue } = useFormikContext();
  const [isFocused, setIsFocused] = React.useState<boolean>(false);

  let state: InputState;
  if (fieldTouched && error) {
    text = error;
    state = 'invalid';
  } else if (fieldTouched && subText) {
    text = subText;
    state = 'valid';
  } else {
    state = 'neutral';
  }

  const handleChange = (event: any) => {
    setFieldValue(field.name, sanitizeHtml(event.target.value));

    // Call the optional onChange prop if provided
    if (onChange) {
      onChange(event);
    }
  };

  const blurHandler = (): void => {
    setFieldTouched(field.name);
    setIsFocused(false);
    // some parent components add an onBlur prop, so we need to call it here
    onBlur && onBlur();
  };

  return (
    <div className={`${styles.fieldContainer} ${state && styles[state]}`}>
      <UnionField
        {...field}
        {...rest}
        ref={inputRef}
        state={state}
        subText={text}
        value={field.value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          blurHandler();
        }}
      />
      {showAiButton && (
        <AiButton
          className={styles.aiButtonPosition}
          field={buildField(field, setFieldValue, values)}
          isCustomPromptInputSingleLine={true}
          isParentFocused={isFocused}
        />
      )}
    </div>
  );
};
